import * as React from 'react';
import {
  MasonryEngineNodeAction,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';

const PAGE_QUERY_PARAM_KEY = 'page';
const PAGINATION_STRATEGY_QUERY_PARAM_KEY = 'paginationStrategy';
const MANAGED_PARAM_KEYS = [
  PAGE_QUERY_PARAM_KEY,
  PAGINATION_STRATEGY_QUERY_PARAM_KEY,
];

type UsePageChangeActionsOptions = {
  onPageChange: MasonryEngineNodeAction | undefined;
  paginationStrategy: 'page' | 'cursor' | undefined;
  nodeId: string;
};

/**
 * Builds the offset and cursor page-change handlers for the paginated
 * chat-session list.
 *
 * Both handlers share the same `extraQueryParams` deduplication logic:
 * any pre-existing `page` or `paginationStrategy` entry is stripped
 * before the fresh values are appended, preventing param accumulation
 * across navigations.
 */
export const usePageChangeActions = ({
  onPageChange,
  paginationStrategy = 'page',
  nodeId,
}: UsePageChangeActionsOptions) => {
  const invokeAction = useMasonryEngineActionEmitter();
  const existingExtraQueryParams = React.useMemo(
    () =>
      (Array.isArray(onPageChange?.payload?.extraQueryParams)
        ? onPageChange.payload.extraQueryParams
        : []
      ).filter(
        (param: { key: string }) => !MANAGED_PARAM_KEYS.includes(param.key),
      ),
    [onPageChange?.payload?.extraQueryParams],
  );

  const buildPageChangePayload = React.useCallback(
    (pageValue: string) => ({
      ...onPageChange?.payload,
      renderIntermediately: 'self',
      extraQueryParams: [
        ...existingExtraQueryParams,
        { key: PAGE_QUERY_PARAM_KEY, value: pageValue },
        { key: PAGINATION_STRATEGY_QUERY_PARAM_KEY, value: paginationStrategy },
      ],
    }),
    [existingExtraQueryParams, onPageChange?.payload, paginationStrategy],
  );

  const handleOffsetPageChange = React.useCallback(
    (page: number) => {
      invokeAction({
        type: 'setNodeState',
        namespace: 'default',
        payload: {
          nodeId,
          state: { currentPage: page },
        },
      });

      if (onPageChange) {
        invokeAction({
          ...onPageChange,
          payload: buildPageChangePayload(String(page)),
        });
      }
    },
    [invokeAction, nodeId, onPageChange, buildPageChangePayload],
  );

  const handleCursorChange = React.useCallback(
    (cursor: string) => {
      if (onPageChange) {
        invokeAction({
          ...onPageChange,
          payload: buildPageChangePayload(cursor),
        });
      }
    },
    [invokeAction, onPageChange, buildPageChangePayload],
  );

  return { handleOffsetPageChange, handleCursorChange };
};
